/**
 * ProjectsHub — unified Projects hub page.
 *
 * Thin wrapper that renders one CockpitPage with:
 *   - Title "Projects · {activeTabLabel}" + per-tab KPI strip in the header
 *   - Underline tab bar (Jobs | Opportunities | Install Readiness)
 *   - Sub-pages rendered in embedded mode (no own header/masthead)
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useDataIsolation } from "@/hooks/useDataIsolation";
import { getAllOpportunities, getOpportunityStats } from "@/data/opportunitySelectors";
import {
  useProjectJobsSummary,
  useProjectOpportunitiesSummary,
  useProjectReadinessSummary,
} from "@/hooks/useProjectServerPaging";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { getJobBlockers } from "@/data/workflowBlockers";
import { canEnterStage } from "@/data/workflowEngine";
import type { Job } from "@/data/types";
import { PROJECTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import {
  CockpitTabs,
  HeroMetric,
  HeroDivider,
} from "@/components/cockpit/CockpitShell";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import JobsListCockpit from "@/pages/JobsListCockpit";
import QuotesListCockpit from "@/pages/QuotesListCockpit";
import InstallReadinessCockpit from "@/pages/InstallReadinessCockpit";

type TabId = "jobs" | "opportunities" | "install-readiness";

const TABS: { id: TabId; label: string }[] = [
  { id: "jobs",              label: "Jobs" },
  { id: "opportunities",    label: "Opportunities" },
  { id: "install-readiness", label: "Install Readiness" },
];

const DEEP_LINKS: Record<TabId, { label: string; path: string }> = {
  "jobs":              { label: "Open Jobs deep route",              path: "/jobs" },
  "opportunities":    { label: "Open Opportunities deep route",    path: "/opportunities" },
  "install-readiness": { label: "Open Install Readiness deep route", path: "/install-readiness" },
};

export default function ProjectsHub() {
  const [activeTab, setActiveTab] = useState<TabId>("jobs");
  const { issues, salesOpportunities, salesOpportunitiesLoading } = useAppData();
  const { jobs } = useDataIsolation();
  const { getPOsByJob } = useVendorPO();
  const { receivingRecords, jobAllocations } = useWarehouseInventory();
  const projectJobsSummary = useProjectJobsSummary({ enabled: PROJECTS_SERVER_PAGING_ENABLED });
  const projectOppSummary = useProjectOpportunitiesSummary({ enabled: PROJECTS_SERVER_PAGING_ENABLED });
  const projectInstallSummary = useProjectReadinessSummary({ enabled: PROJECTS_SERVER_PAGING_ENABLED });
  const projectKpiJobs = jobs;

  /* ── Jobs KPIs ───────────────────────────────────────────────── */
  const jobsKpi = useMemo(() => {
    const active = projectKpiJobs.filter(j => j.currentStage !== "Completed");
    const blocked = active.filter(j => {
      const oi = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      return getJobBlockers(j, oi).some(b => b.severity === "critical");
    });
    const ready = active.filter(j => {
      const materialsDone = j.materialReadiness === "Fully Received" || j.materialReadiness === "Loaded for Crew";
      const crewDone = j.assignmentType !== "Unassigned";
      const oi = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      return materialsDone && crewDone && getJobBlockers(j, oi).length === 0;
    });
    const atRisk = active.filter(j => {
      const permitDone = !j.permitRequired || j.permitStatus === "Approved";
      const materialsDone = j.materialReadiness === "Fully Received" || j.materialReadiness === "Loaded for Crew";
      const crewDone = j.assignmentType !== "Unassigned";
      const qaDone = !!j.qaSubmitted;
      const doneCount = [permitDone, materialsDone, crewDone, qaDone].filter(Boolean).length;
      const percent = Math.round((doneCount / 4) * 100);
      const oi = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      const isBlocked = getJobBlockers(j, oi).some(b => b.severity === "critical");
      return percent > 0 && percent < 75 && !isBlocked;
    }).length;
    const pctReady = active.length > 0 ? Math.round((ready.length / active.length) * 100) : 0;
    const fallback = { active: active.length, blocked: blocked.length, pctReady, atRisk };
    if (PROJECTS_SERVER_PAGING_ENABLED && projectJobsSummary.data) {
      return {
        active: projectJobsSummary.data.active,
        blocked: projectJobsSummary.data.blocked,
        pctReady: projectJobsSummary.data.pctReady,
        atRisk: projectJobsSummary.data.atRisk,
      };
    }
    return fallback;
  }, [projectKpiJobs, issues, projectJobsSummary.data]);

  /* ── Opportunities KPIs ──────────────────────────────────────── */
  const oppsKpi = useMemo(() => {
    const enriched = getAllOpportunities(salesOpportunities);
    const stats = getOpportunityStats(enriched, projectKpiJobs);
    const fallback = {
      pipelineValue: stats.pipelineValue,
      open: stats.openCount,
      followUp: stats.followUpCount,
      aging14: stats.aging14,
      closeRate: stats.closeRate,
    };
    if (PROJECTS_SERVER_PAGING_ENABLED && projectOppSummary.data) {
      return {
        pipelineValue: projectOppSummary.data.pipelineValue,
        open: projectOppSummary.data.open,
        followUp: projectOppSummary.data.followUp,
        aging14: projectOppSummary.data.aging14,
        closeRate: projectOppSummary.data.closeRate,
      };
    }
    return fallback;
  }, [salesOpportunities, projectKpiJobs, projectOppSummary.data]);

  /* ── Install Readiness KPIs ──────────────────────────────────── */
  const IN_INSTALL_STAGES: Array<Job["currentStage"]> = [
    "Assigned and Dispatched",
    "In Progress",
    "QA Review",
  ];

  const installKpi = useMemo(() => {
    const active = projectKpiJobs.filter(j => j.currentStage !== "Completed" && j.currentStage !== "Issue");
    let ready = 0, almost = 0, blocked = 0, inInstall = 0;
    for (const job of active) {
      const pos = getPOsByJob(job.id);
      const jobReceiving = receivingRecords.filter(r => r.jobId === job.id);
      const jobAllocs = jobAllocations.filter(a => a.jobId === job.id);
      const jobIssues = issues.filter(i => i.jobId === job.id && i.status !== "Resolved");

      const hardList: boolean[] = [];
      const softList: boolean[] = [];

      const reviewGate = canEnterStage(job, "Ready for Ordering");
      if (!reviewGate.met) reviewGate.blockers.forEach(() => hardList.push(true));
      if (pos.length === 0 && !job.planningStatus?.includes("Ordered") && (job.approvedForOrdering || job.intakeApproved)) {
        hardList.push(true);
      }
      for (const po of pos) {
        const poRec = jobReceiving.filter(r => r.poId === po.poId);
        const allRec = poRec.length > 0 && poRec.every(r => r.status === "Fully Received");
        if (!allRec) hardList.push(true);
      }
      const fullyReceived = jobReceiving.filter(r => r.status === "Fully Received");
      const unallocated = fullyReceived.filter(r => !jobAllocs.find(a => a.sku === r.sku));
      if (unallocated.length > 0) softList.push(true);
      if (jobIssues.length > 0) {
        if (jobIssues.some(i => i.severity === "Urgent")) hardList.push(true);
        else softList.push(true);
      }

      if (IN_INSTALL_STAGES.includes(job.currentStage)) inInstall++;
      else if (hardList.length === 0 && softList.length === 0) ready++;
      else if (hardList.length === 0) almost++;
      else blocked++;
    }
    const fallback = { ready, almost, blocked, inInstall };
    if (PROJECTS_SERVER_PAGING_ENABLED && projectInstallSummary.data) {
      return {
        ready: projectInstallSummary.data.ready,
        almost: projectInstallSummary.data.almost,
        blocked: projectInstallSummary.data.blocked,
        inInstall: projectInstallSummary.data.inInstall,
      };
    }
    return fallback;
  }, [projectKpiJobs, issues, getPOsByJob, receivingRecords, jobAllocations, projectInstallSummary.data]);

  /* ── KPI strip per active tab ────────────────────────────────── */
  function renderKpi() {
    if (activeTab === "jobs") {
      return (
        <>
          <HeroMetric label="Active" value={jobsKpi.active} hero />
          <HeroDivider />
          <HeroMetric label="Ready" value={`${jobsKpi.pctReady}%`} tone={jobsKpi.pctReady >= 50 ? "good" : "default"} />
          <HeroMetric label="Blocked" value={jobsKpi.blocked} tone={jobsKpi.blocked > 0 ? "bad" : "default"} />
          <HeroMetric label="At Risk" value={jobsKpi.atRisk} tone={jobsKpi.atRisk > 0 ? "warn" : "default"} />
        </>
      );
    }
    if (activeTab === "opportunities") {
      const pv = oppsKpi.pipelineValue;
      return (
        <>
          <HeroMetric label="Pipeline" value={`$${(pv / 1000).toFixed(pv >= 100_000 ? 0 : 1)}k`} hero />
          <HeroDivider />
          <HeroMetric label="Open" value={oppsKpi.open} />
          <HeroMetric label="Follow-up" value={oppsKpi.followUp} />
          <HeroMetric label="Aging > 14d" value={oppsKpi.aging14} tone={oppsKpi.aging14 > 0 ? "warn" : "default"} />
          <HeroMetric label="Close rate" value={`${oppsKpi.closeRate}%`} />
        </>
      );
    }
    return (
      <>
        <HeroMetric label="Ready" value={installKpi.ready} hero tone="good" />
        <HeroDivider />
        <HeroMetric label="Almost" value={installKpi.almost} tone="warn" />
        <HeroMetric label="Blocked" value={installKpi.blocked} tone={installKpi.blocked > 0 ? "bad" : "default"} />
        <HeroMetric label="In Install" value={installKpi.inInstall} tone="good" />
      </>
    );
  }

  const activeTabLabel = TABS.find(t => t.id === activeTab)?.label;

  return (
    <CockpitPage
      title="Projects"
      titleSuffix={activeTabLabel}
      maxWidth="7xl"
      controls={
        <div className="flex items-center gap-7">
          <div className="flex items-center gap-3">
            {renderKpi()}
          </div>
          <Link
            to={DEEP_LINKS[activeTab].path}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline whitespace-nowrap -mt-4"
          >
            {DEEP_LINKS[activeTab].label} ↗
          </Link>
        </div>
      }
      tabs={
        <CockpitTabs
          tabs={TABS.map(t => ({ value: t.id, label: t.label }))}
          value={activeTab}
          onChange={(v) => setActiveTab(v as TabId)}
        />
      }
    >
      {activeTab === "jobs"              && (
        <JobsListCockpit embedded />
      )}
      {activeTab === "opportunities"    && (
        <QuotesListCockpit
          embedded
          jobsOverride={PROJECTS_SERVER_PAGING_ENABLED ? undefined : projectKpiJobs}
          opportunitiesOverride={PROJECTS_SERVER_PAGING_ENABLED ? undefined : salesOpportunities}
          opportunitiesLoading={PROJECTS_SERVER_PAGING_ENABLED ? false : salesOpportunitiesLoading}
        />
      )}
      {activeTab === "install-readiness" && (
        <InstallReadinessCockpit
          embedded
          jobsOverride={PROJECTS_SERVER_PAGING_ENABLED ? undefined : projectKpiJobs}
        />
      )}
    </CockpitPage>
  );
}
